import React, {useState, useMemo} from 'react';
import styled, {css} from 'styled-components';

import ContentWrapper from 'components/contentWrapper';
import ProductPriceBox from 'components/pages/product/productPriceBox';
import ProductPriceTable from 'components/pages/product/productPriceTable';
import ContentArea from 'components/contentArea';

import theme, {bp} from 'settings/theme';


type Props = {
  product: any
};


type PricingModel = {
  initialOpenPanel: string | null,
  buy? : {
    purchasePrice?: number,
    purchasePriceVat?: number,
    setupAndDeliveryIncluded?: boolean
  },
  buyUsed? : {
    purchasePrice?: number,
    purchasePriceVat?: number,
    setupAndDeliveryIncluded?: boolean
  },
  motability?: {
    weeklyPrice?: number,
    weeklyPriceVat?: number
  },
  shortTermHire?: {
    price1?: number,
    price1Vat?: number,
    price2?: number,
    price2Vat?: number,
    price3?: number,
    price3Vat?: number
  },
  longTermHire?: {
    oneYearInitialPrice?: number,
    oneYearInitialPriceVat?: number,
    oneYearWeeklyPrice?: number,
    oneYearWeeklyPriceVat?: number,
    threeYearInitialPrice?: number,
    threeYearInitialPriceVat?: number,
    threeYearWeeklyPrice?: number,
    threeYearWeeklyPriceVat?: number
  }
};


type BoxStates = {
  buy?: {isOpen: boolean},
  buyUsed?: {isOpen: boolean},
  motability?: {isOpen: boolean},
  shortTermHire?: {isOpen: boolean},
  longTermHire?: {isOpen: boolean},
};


const initialBoxStates = {
  buy: {isOpen: false},
  buyUsed: {isOpen: false},
  motability: {isOpen: false},
  shortTermHire: {isOpen: false},
  longTermHire: {isOpen: false}
};

const formatPrice = (value) => {
  let price: any = (Math.round((value ?? 0) * 100) / 100);        // Sets precision to 2 - e.g. removes lots of extra numbers
  price = price.toFixed(2);                                // Makes it 2 decimal places
  price = price.replace(/(\.|,)00$/g, '');                 // Removes double zero after decimal point - e.g. 37.00 becomes 37
  price = price.replace(/\B(?=(\d{3})+(?!\d))/g, ",");     // Adds commas into thousands and millions
  return price;
};

const addVat = (value) => {
  return value * 1.2;
};



// Product Header
const Component: React.FC<Props> = (props) => {

  const [boxStates, setBoxStates] = useState<BoxStates>(initialBoxStates)

  // Transform Pricing
  const pricing = useMemo(() => {

    let pricing: PricingModel = {initialOpenPanel: null}
    let newBoxStates = boxStates
    const pricingArr = props.product?.content?.pricing_structure || []

    // Run through each type of pricing in the array and convert it to an object
    pricingArr.forEach(item => {

      // Buy
      if(item.component === 'buy'){

        pricing.buy = {
          purchasePrice: parseFloat(item.purchase_price),
          purchasePriceVat: addVat(parseFloat(item.purchase_price)),
          setupAndDeliveryIncluded: item.setup_and_delivery_included === 'yes'
        };

        pricing.initialOpenPanel = 'buy';

      }

      // Buy Used
      if(item.component === 'buy-used'){

        pricing.buyUsed = {
          purchasePrice: parseFloat(item.purchase_price),
          purchasePriceVat: addVat(parseFloat(item.purchase_price)),
          setupAndDeliveryIncluded: item.setup_and_delivery_included === 'yes'
        };

        pricing.initialOpenPanel = 'buyUsed';

      }

      // Motability
      if(item.component == 'motability'){

        pricing.motability = {
          weeklyPrice: parseFloat(item.weekly_price),
          weeklyPriceVat: addVat(parseFloat(item.weekly_price))
        };

        if(!pricing.initialOpenPanel){
          pricing.initialOpenPanel = 'motability'   
        };
          
      }

      // Short Term Hire
      if(item.component == 'short-term-hire'){

        pricing.shortTermHire = {
          price1: parseFloat(item.price_1),
          price1Vat: addVat(parseFloat(item.price_1)),
          price2: parseFloat(item.price_2),
          price2Vat: addVat(parseFloat(item.price_2)),
          price3: parseFloat(item.price_3),
          price3Vat: addVat(parseFloat(item.price_3))
        };

        if(!pricing.initialOpenPanel){
          pricing.initialOpenPanel = 'shortTermHire'
        };

      }

      // Long Term Hire
      if(item.component == 'long-term-hire'){

        pricing.longTermHire = {
          oneYearInitialPrice: parseFloat(item.one_year_initial_price),
          oneYearInitialPriceVat: addVat(parseFloat(item.one_year_initial_price)),
          oneYearWeeklyPrice: parseFloat(item.one_year_weekly_price),
          oneYearWeeklyPriceVat: addVat(parseFloat(item.one_year_weekly_price)),
          threeYearInitialPrice: parseFloat(item.three_year_initial_price),
          threeYearInitialPriceVat: addVat(parseFloat(item.three_year_initial_price)),
          threeYearWeeklyPrice: parseFloat(item.three_year_weekly_price),
          threeYearWeeklyPriceVat: addVat(parseFloat(item.three_year_weekly_price))
        };

        if(!pricing.initialOpenPanel){
          pricing.initialOpenPanel = 'longTermHire'
        };

      }

    });


    // If none of the boxes are currently set to open then open the first one
    if(
      !newBoxStates.buy?.isOpen && 
      !newBoxStates.buyUsed?.isOpen && 
      !newBoxStates.motability?.isOpen && 
      !newBoxStates.shortTermHire?.isOpen && 
      !newBoxStates.longTermHire?.isOpen &&
      pricing.initialOpenPanel
    ){
      newBoxStates[pricing.initialOpenPanel].isOpen = true;
    }

    setBoxStates(newBoxStates);

    return pricing;

  }, [props.product.pricing_structure]);


  // Toggle Panel
  const togglePanel = (name) => {

    setBoxStates({
      ...boxStates,
      [name]: {
        ...boxStates[name],
        isOpen: !boxStates[name].isOpen
      }
    });

  };

  return (

    <StyledComponent className="productPricing spacingTop">

      <ContentWrapper>
        <h3 className="spacingBottomHalf">Pricing</h3>
        </ContentWrapper>

      <ContentWrapper fullWidthAt={[1,2]}>
        <div className="productPricing-boxes">

          {/* Buy */}
          {pricing.buy?.purchasePrice && 
            <div className="productPricing-box">
              <ProductPriceBox title="Purchase Price" name="buy" isOpen={boxStates.buy!.isOpen} togglePanel={togglePanel}>
                  
                <div className="paddingHorizontal paddingVertical">
                  <div className="productPricing-largePrice">£{formatPrice(pricing.buy?.purchasePrice)} <small>exc VAT</small> / £{formatPrice(pricing.buy?.purchasePriceVat)} <small>inc VAT</small></div>
                  {pricing.buy?.setupAndDeliveryIncluded &&
                    <div className="productPricing-notes">Including technician set-up and delivery</div>
                  }
                </div>

              </ProductPriceBox>  
            </div>
          }

          {/* Buy Used */}
          {pricing.buyUsed?.purchasePrice && 
            <div className="productPricing-box">
              <ProductPriceBox title="Purchase Price (Used)" name="buy" isOpen={boxStates.buyUsed!.isOpen} togglePanel={togglePanel}>
                  
                <div className="paddingHorizontal paddingVertical">
                  <div className="productPricing-largePrice">£{formatPrice(pricing.buyUsed?.purchasePrice)} <small>exc VAT</small> / £{formatPrice(pricing.buyUsed?.purchasePriceVat)} <small>inc VAT</small></div>
                  {pricing.buyUsed?.setupAndDeliveryIncluded &&
                    <div className="productPricing-notes">Including technician set-up and delivery</div>
                  }
                </div>

              </ProductPriceBox>  
            </div>
          }

          
          {/* Motability */}
          {pricing.motability && 
            <div className="productPricing-box">
              <ProductPriceBox logoUrl="https://a.storyblok.com/f/76714/304x62/9ac6707301/motability-logo.png" name="motability" isOpen={boxStates.motability!.isOpen} togglePanel={togglePanel}>
                <ProductPriceTable>
                  <thead>
                    <tr>
                      <th>Contract Length</th>
                      <th>Weekly Charge</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>3 Years</td>
                      <td>
                        <div>£{formatPrice(pricing.motability.weeklyPrice)} <small>(ex VAT)</small></div>
                        {/* <div>£{formatPrice(pricing.motability.weeklyPriceVat)} <small>(inc VAT)</small></div> */}
                      </td>
                    </tr>
                  </tbody>
                </ProductPriceTable>
              </ProductPriceBox>  
            </div>
          }

          
          {/* Short Term Hire */}
          {pricing.shortTermHire &&
            <div className="productPricing-box">
              <ProductPriceBox title="Short Term Hire Per Week" isOpen={boxStates.shortTermHire!.isOpen} name="shortTermHire" togglePanel={togglePanel}>
                <ProductPriceTable>
                  <thead>
                    <tr>
                      <th>Weeks 1-12</th>
                      <th>Weeks 13-52</th>
                      <th>After Week 52</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>£{formatPrice(pricing.shortTermHire.price1)} <small>(ex VAT)</small></div>
                        <div>£{formatPrice(pricing.shortTermHire.price1Vat)} <small>(inc VAT)</small></div>
                      </td>
                      <td>
                        <div>£{formatPrice(pricing.shortTermHire.price2)} <small>(ex VAT)</small></div>
                        <div>£{formatPrice(pricing.shortTermHire.price2Vat)} <small>(inc VAT)</small></div>
                      </td>
                      <td>
                        <div>£{formatPrice(pricing.shortTermHire.price3)} <small>(ex VAT)</small></div>
                        <div>£{formatPrice(pricing.shortTermHire.price3Vat)} <small>(inc VAT)</small></div>
                      </td>
                    </tr>
                  </tbody>
                </ProductPriceTable>
              </ProductPriceBox>  
            </div>
          }

          
          {/* Long Term Hire */}
          {pricing.longTermHire &&
            <div className="productPricing-box">
              <ProductPriceBox title="Long Term Hire Per Week" isOpen={boxStates.longTermHire!.isOpen} name="longTermHire" togglePanel={togglePanel}>
                <ProductPriceTable>
                  <thead>
                    <tr>
                      <th>Contract Length</th>
                      <th>Initial Payment</th>
                      <th>Weekly Charge</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>1 Year</td>
                      <td>
                        <div>£{formatPrice(pricing.longTermHire.oneYearInitialPrice)} <small>(ex VAT)</small></div>
                        <div>£{formatPrice(pricing.longTermHire.oneYearInitialPriceVat)} <small>(inc VAT)</small></div>
                      </td>
                      <td>
                        <div>£{formatPrice(pricing.longTermHire.oneYearWeeklyPrice)} <small>(ex VAT)</small></div>
                        <div>£{formatPrice(pricing.longTermHire.oneYearWeeklyPriceVat)} <small>(inc VAT)</small></div>
                      </td>
                    </tr>
                    <tr>
                      <td>3 Years</td>
                      <td>
                        <div>£{formatPrice(pricing.longTermHire.threeYearInitialPrice)} <small>(ex VAT)</small></div>
                        <div>£{formatPrice(pricing.longTermHire.threeYearInitialPriceVat)} <small>(inc VAT)</small></div>
                      </td>
                      <td>
                        <div>£{formatPrice(pricing.longTermHire.threeYearWeeklyPrice)} <small>(ex VAT)</small></div>
                        <div>£{formatPrice(pricing.longTermHire.threeYearWeeklyPriceVat)} <small>(inc VAT)</small></div>
                      </td>
                    </tr>
                  </tbody>
                </ProductPriceTable>
              </ProductPriceBox>  
            </div>
          }

        </div>
      </ContentWrapper>

      {/* Content */}
      {props.product?.content?.pricing_content && Array.isArray(props.product.content.pricing_content) &&
        <div className="productPricing-content">
          <ContentArea content={props.product.content.pricing_content} />
        </div>
      }

    </StyledComponent>
  
  );

};


export default Component;




// Styles
const StyledComponent = styled.div`

  display: block;

  .productPricing-boxes{
    display: flex;
    flex-direction: column;
  }

  .productPricing-box{
    margin-bottom: 20px;
  }

  .productPricing-largePrice{

    margin-bottom: 0.5rem;
    font-size: 16px;
    font-weight: bold;

    small{
      font-weight: normal;
    }

  }

  .productPricing-notes{
    font-size: 12px;
  }

  .productPricing-content{
    margin-top: -2rem;
  }

  @media (min-width: ${bp(3)}) {

    .productPricing-boxes{
      flex-direction: row;
      flex-wrap: wrap;
      margin-left: -15px;
      margin-right: -15px;
    }

    .productPricing-box{
      width: 50%;
      margin-bottom: 30px;
      padding-left: 15px;
      padding-right: 15px;
    }
    
  }

  @media (min-width: ${bp(4)}) {

    .productPricing-boxes{
      margin-left: -30px;
      margin-right: -30px;
    }

    .productPricing-box{
      margin-bottom: 40px;
      padding-left: 30px;
      padding-right: 30px;
    }


    .productPricing-largePrice{
      font-size: 18px;
    }

    .productPricing-notes{
      font-size: 14px;
    }

  }

`;



